import { type Result } from '@shared/all/domains/request';
import { TRPCClientError } from '@trpc/client';

import type { AppRouter } from '../../../apps/server/src/router';

/**
 * Wraps a promise that may throw into a Result tuple.
 * Used by the tRPC client proxy to convert throwing behavior to Result tuples.
 *
 * @param promise - A promise that may throw a TRPCClientError
 * @returns A Result tuple: [error, undefined] on error, [undefined, data] on success
 */
export async function wrapToResult<T>(promise: Promise<T>): Promise<Result<T>> {
  try {
    const result = await promise;
    return [undefined, result];
  } catch (error) {
    if (error instanceof TRPCClientError) {
      return [error as TRPCClientError<AppRouter>, undefined];
    }
    throw error;
  }
}

/**
 * Unwraps a Result tuple back to the data, throwing on error.
 * Used with React Query's queryFn which expects the data directly.
 *
 * @param fn - A promise that resolves to a Result tuple
 * @returns The unwrapped data, or throws an error
 */
export async function unwrapFromResult<T>(fn: Promise<Result<T>>): Promise<T> {
  const result = await fn;
  const [error, data] = result;
  if (error) throw new Error(error.message);
  return data;
}
